import { useEffect, useId, useRef, useState } from 'react';
import { apiGet } from '../game/api';
import { PRIVATE_DEFAULTS, type DifficultyId, type PrivateOptions, type TeamFormat } from '../../../shared/modes';
import { t, type Key } from '../i18n';
import { ModeIcon } from './icons';

export type ModeChoice =
  | { mode: 'ffa' }
  | { mode: 'tdm' | 'dom'; format: TeamFormat }
  | { mode: 'survival'; difficulty: DifficultyId }
  | { mode: 'private'; code?: string; create?: PrivateOptions };

/** Humains présents par file publique, rafraîchi toutes les 5 s (pour choisir une file active). */
function useQueues(): Record<string, number> {
  const [q, setQ] = useState<Record<string, number>>({});
  useEffect(() => {
    const load = () => void apiGet<{ queues: Record<string, number> }>('/api/queues').then((r) => setQ(r.queues ?? {})).catch(() => {});
    load();
    const id = window.setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);
  return q;
}

const count = (q: Record<string, number>, prefix: string) => Object.entries(q).reduce((n, [k, v]) => (k === prefix || k.startsWith(`${prefix}:`) ? n + v : n), 0);

export const DIFFS: DifficultyId[] = ['facile', 'normal', 'moyen', 'difficile', 'ultra', 'tankshift'];
const KINDS: ModeChoice['mode'][] = ['ffa', 'tdm', 'dom', 'survival', 'private'];

const defaultChoice = (k: ModeChoice['mode']): ModeChoice =>
  k === 'tdm' || k === 'dom' ? { mode: k, format: '2v2' } : k === 'survival' ? { mode: 'survival', difficulty: 'normal' } : k === 'private' ? { mode: 'private', code: '' } : { mode: 'ffa' };

/** Libellé court du choix en cours (sous le bouton Jouer). */
export function modeSummary(m: ModeChoice): string {
  const name = t(`mode_${m.mode}` as Key);
  if (m.mode === 'tdm' || m.mode === 'dom') return `${name} · ${m.format}`;
  if (m.mode === 'survival') return `${name} · ${t(`diff_${m.difficulty}` as Key)}`;
  if (m.mode === 'private') return m.create ? `${name} · ${t('privCreate')}` : m.code ? `${name} · ${m.code}` : name;
  return name;
}

/** Commutateur accessible (rôle switch). */
export function Switch({ on, onChange, label, id }: { on: boolean; onChange: (v: boolean) => void; label?: string; id?: string }) {
  return (
    <button type="button" role="switch" id={id} aria-checked={on} aria-label={label} className="switch" onClick={() => onChange(!on)}>
      <span className="switch-track" aria-hidden />
      <span>{on ? t('on') : t('off')}</span>
    </button>
  );
}

/** Choix du mode : file commune par objectif et format ; salons privés par code ou création. */
export function ModePicker({ value, onChange, loggedIn }: { value: ModeChoice; onChange: (m: ModeChoice) => void; loggedIn: boolean }) {
  const [privTab, setPrivTab] = useState<'join' | 'create'>('join');
  const [code, setCode] = useState('');
  const [opts, setOpts] = useState<PrivateOptions>(PRIVATE_DEFAULTS);
  const queues = useQueues();
  const uid = useId();
  const rows = useRef<(HTMLButtonElement | null)[]>([]);
  const set = (patch: Partial<PrivateOptions>) => {
    const next = { ...opts, ...patch };
    setOpts(next);
    onChange({ mode: 'private', create: next });
  };
  const players = (n: number) => (n > 0 ? <small className="num">{t('queuePlayers', { n })}</small> : null);
  // Flèches haut/bas : passer d'un mode à l'autre comme dans un groupe de boutons radio.
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const d = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const j = (i + d + KINDS.length) % KINDS.length;
    onChange(defaultChoice(KINDS[j]));
    rows.current[j]?.focus();
  };
  return (
    <div className="mode-picker">
      <div className="mode-list" role="radiogroup" aria-label={t('hangarModes')}>
        {KINDS.map((k, i) => {
          const on = value.mode === k;
          const n = k === 'private' ? 0 : count(queues, k);
          return (
            <div key={k} className={`mode-item ${on ? 'on' : ''}`}>
              <button
                ref={(el) => {
                  rows.current[i] = el;
                }}
                type="button"
                role="radio"
                aria-checked={on}
                tabIndex={on ? 0 : -1}
                aria-labelledby={`${uid}-${k}-n ${uid}-${k}-c`}
                aria-describedby={`${uid}-${k}-d`}
                className="mode-row"
                onClick={() => !on && onChange(defaultChoice(k))}
                onKeyDown={(e) => onKey(e, i)}
              >
                <ModeIcon mode={k} />
                <span className="mode-text">
                  <span className="mode-name display" id={`${uid}-${k}-n`}>
                    {t(`mode_${k}` as Key)}
                  </span>
                  <span className="mode-desc" id={`${uid}-${k}-d`}>
                    {t(`modeDesc_${k}` as Key)}
                  </span>
                </span>
                <span className={`mode-count ${n > 0 ? 'live' : ''}`} id={`${uid}-${k}-c`}>
                  {n > 0 ? t('queuePlayers', { n }) : ''}
                </span>
              </button>
              {on && (k === 'tdm' || k === 'dom') && value.mode === k && (
                <div className="mode-options">
                  <span className="eyebrow">{t('modeFormat')}</span>
                  <div className="seg">
                    {(['2v2', '4v4', '8v8'] as TeamFormat[]).map((f) => (
                      <button key={f} type="button" aria-pressed={value.format === f} onClick={() => onChange({ mode: k, format: f })}>
                        {f} {players(queues[`${k}:${f}`] ?? 0)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {on && k === 'survival' && value.mode === 'survival' && (
                <div className="mode-options">
                  <span className="eyebrow">{t('modeDifficulty')}</span>
                  <div className="seg seg-diff">
                    {DIFFS.map((d, di) => (
                      <button key={d} type="button" aria-pressed={value.difficulty === d} onClick={() => onChange({ mode: 'survival', difficulty: d })}>
                        <span className="diff-gauge" aria-hidden>
                          {DIFFS.map((_, gi) => (
                            <i key={gi} className={gi <= di ? 'on' : ''} />
                          ))}
                        </span>
                        {t(`diff_${d}` as Key)} {players(queues[`survival:${d}`] ?? 0)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {on && k === 'private' && (
                <div className="mode-options private-box">
                  <div className="seg" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={privTab === 'join'}
                      onClick={() => {
                        setPrivTab('join');
                        onChange({ mode: 'private', code });
                      }}
                    >
                      {t('privJoin')}
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={privTab === 'create'}
                      disabled={!loggedIn}
                      onClick={() => {
                        setPrivTab('create');
                        onChange({ mode: 'private', create: opts });
                      }}
                    >
                      {t('privCreate')}
                    </button>
                  </div>
                  {privTab === 'join' ? (
                    <input
                      className="name-input code-input"
                      placeholder={t('privCode')}
                      aria-label={t('privCode')}
                      value={code}
                      maxLength={6}
                      autoCapitalize="characters"
                      spellCheck={false}
                      onChange={(e) => {
                        const c = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                        setCode(c);
                        onChange({ mode: 'private', code: c });
                      }}
                    />
                  ) : (
                    <div className="settings-grid compact">
                      <label htmlFor={`${uid}-map`}>{t('privMap')}</label>
                      <select id={`${uid}-map`} value={opts.map} onChange={(e) => set({ map: e.target.value as PrivateOptions['map'] })}>
                        <option value="A">{t('map_A')}</option>
                        <option value="B">{t('map_B')}</option>
                        <option value="C">{t('map_C')}</option>
                        <option value="T">{t('map_T')}</option>
                      </select>
                      <label htmlFor={`${uid}-teams`}>{t('privTeams')}</label>
                      <Switch id={`${uid}-teams`} on={opts.teams} onChange={(v) => set({ teams: v })} />
                      <label htmlFor={`${uid}-max`}>{t('privMax')}</label>
                      <input id={`${uid}-max`} className="field" type="number" min={1} max={40} value={opts.maxPlayers} onChange={(e) => set({ maxPlayers: Number(e.target.value) })} />
                      <label htmlFor={`${uid}-bots`}>{t('privBots')}</label>
                      <input id={`${uid}-bots`} className="field" type="number" min={0} max={20} value={opts.bots} onChange={(e) => set({ bots: Number(e.target.value) })} />
                      <label htmlFor={`${uid}-xp`}>{t('privXp')}</label>
                      <input id={`${uid}-xp`} className="field" type="number" min={0.5} max={5} step={0.5} value={opts.xpMul} onChange={(e) => set({ xpMul: Number(e.target.value) })} />
                      <label htmlFor={`${uid}-mon`}>{t('privMonsters')}</label>
                      <Switch id={`${uid}-mon`} on={opts.monsters} onChange={(v) => set({ monsters: v })} />
                      <label htmlFor={`${uid}-boss`}>{t('privBoss')}</label>
                      <Switch id={`${uid}-boss`} on={opts.boss} onChange={(v) => set({ boss: v })} />
                    </div>
                  )}
                  <p className="wip">{t('privInfo')}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
